// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { sparseCustomerSchema } from '@/sparse/api/types';
import { parseOneAddress } from 'email-addresses';
import { z } from 'zod';

function isEmailAddress(input: string) {
  const parsed = parseOneAddress({ input, rejectTLD: true });
  return parsed !== null;
}

export const emailSchema = z
  .string()
  .nullable()
  .refine(
    (value) =>
      value === null || value.trim().length === 0 || isEmailAddress(value),
    {
      message: 'Invalid email address',
    }
  );

const CONTACT_NAME_MAX_LENGTH = 150;
const CONTACT_EMAIL_MAX_LENGTH = 254;
const CONTACT_PHONE_MAX_LENGTH = 50;
const CONTACT_TITLE_MAX_LENGTH = 150;
const CONTACT_COMMENTS_MAX_LENGTH = 2000;

const phoneSchema = z
  .string()
  .max(CONTACT_PHONE_MAX_LENGTH, 'Phone cannot exceed 50 characters')
  .nullable();

export const contactFormSchema = z.object({
  customer: sparseCustomerSchema.nullable().refine((value) => value !== null, {
    message: 'Company is required',
  }),
  firstName: z
    .string()
    .min(1, 'First name is required')
    .max(CONTACT_NAME_MAX_LENGTH, 'First name cannot exceed 150 characters'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .max(CONTACT_NAME_MAX_LENGTH, 'Last name cannot exceed 150 characters'),
  title: z
    .string()
    .max(CONTACT_TITLE_MAX_LENGTH, 'Title cannot exceed 150 characters')
    .nullable(),
  email: emailSchema.refine(
    (value) => value === null || value.length <= CONTACT_EMAIL_MAX_LENGTH,
    {
      message: 'Email cannot exceed 254 characters',
    }
  ),
  phone: phoneSchema,
  comments: z
    .string()
    .max(CONTACT_COMMENTS_MAX_LENGTH, 'Comments cannot exceed 2000 characters')
    .nullable(),
});

export type ContactFormData = z.input<typeof contactFormSchema>;
export type ContactFormOutputData = z.output<typeof contactFormSchema>;

export function normalizeNullable(value: string | null) {
  if (value === null) {
    return null;
  }

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}
